import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { useAuthStore } from '@/store/authStore';
import { useWebsockets } from '@/hooks/useWebsockets';
import {
  startVersionGeneration,
  getVersionGenerationJob,
  listActiveVersionGenerations,
  isTerminalGenerationStatus,
  type VersionGenerationJob,
} from '@/api/agentVersionGeneration';
import { AgentVersionGenerationPopups } from './AgentVersionGenerationPopups';
import { VersionPromptModal } from './VersionPromptModal';

interface StartGenerationArgs {
  agentId: string;
  isPublic: boolean;
  agentName: string;
  generate: Record<string, unknown>;
  version: Record<string, unknown>;
  saveAsDraft?: boolean;
}

type StartGenerationResult =
  | { outcome: 'accepted'; job: VersionGenerationJob }
  | { outcome: 'rejected' }
  /** 409 `stale_base_version`: a newer version exists than the editor loaded. No job was created. */
  | { outcome: 'stale' }
  | { outcome: 'ambiguous' };

interface AgentVersionGenerationContextValue {
  /** Start generation, distinguishing a definite client rejection from an
   * ambiguous transport/server outcome that may already have created a job. */
  startGeneration: (args: StartGenerationArgs) => Promise<StartGenerationResult>;
  /** True while this teammate has a non-terminal generation job. */
  hasActiveGeneration: (agentId: string) => boolean;
}

const AgentVersionGenerationContext = createContext<AgentVersionGenerationContextValue | null>(null);

const GENERATION_POLL_INITIAL_DELAY_MS = 5000;
const GENERATION_POLL_BACKOFF_STEP_MS = 5000;
const GENERATION_POLL_BACKOFF_INTERVAL_MS = 60000;
const GENERATION_POLL_MAX_DELAY_MS = 30000;

/**
 * Global tracker for async Teammate Studio "Save Changes" generations.
 *
 * Renders the bottom-right progress popups and the prompt viewer. Resumes
 * in-flight jobs on mount (refresh / navigation recovery) and keeps them in
 * sync via the workspace WebSocket, with a polling fallback in case a socket
 * event is missed.
 *
 * Popups are only painted on Teammate Studio routes: the stack is `fixed`
 * with a high z-index and would otherwise intercept clicks on unrelated pages
 * (e.g. Policy Rules save). Jobs keep tracking in the background and reappear
 * when the user returns to Teammate Studio.
 */
export function AgentVersionGenerationProvider({ children }: { children: React.ReactNode }) {
  const { currentWorkspace } = useAuthStore();
  const workspaceId = currentWorkspace?.id ?? null;
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const showPopups = pathname.startsWith('/teammate-studio');

  // Tracked jobs keyed by job id (includes terminal-but-not-dismissed jobs).
  const [jobs, setJobs] = useState<Record<string, VersionGenerationJob>>({});
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [openJob, setOpenJob] = useState<VersionGenerationJob | null>(null);

  // Avoid duplicate in-flight refreshes for the same job.
  const refreshing = useRef<Set<string>>(new Set());

  // Leaving Teammate Studio: drop the prompt modal so it can't overlay Rules/etc.
  useEffect(() => {
    if (!showPopups) setOpenJob(null);
  }, [showPopups]);

  const upsertJob = useCallback((job: VersionGenerationJob) => {
    setJobs((prev) => ({ ...prev, [job.id]: job }));
  }, []);

  const invalidateForAgent = useCallback(
    (agentId: string) => {
      // The new version touches agent detail + version lists + linked
      // memories. Match any cached query whose key references this agent id.
      queryClient.invalidateQueries({
        predicate: (query) => {
          try {
            return JSON.stringify(query.queryKey).includes(agentId);
          } catch {
            return false;
          }
        },
      });
    },
    [queryClient]
  );

  const refreshJob = useCallback(
    async (agentId: string, jobId: string) => {
      if (refreshing.current.has(jobId)) return;
      refreshing.current.add(jobId);
      try {
        const fresh = await getVersionGenerationJob(agentId, jobId);
        upsertJob(fresh);
        if (isTerminalGenerationStatus(fresh.status)) {
          if (fresh.status !== 'failed') {
            invalidateForAgent(fresh.agent_id);
          }
          // Keep the modal in sync if it's showing this job.
          setOpenJob((prev) => (prev && prev.id === fresh.id ? fresh : prev));
        }
      } catch {
        // Best-effort — the WS event or next poll tick will retry.
      } finally {
        refreshing.current.delete(jobId);
      }
    },
    [upsertJob, invalidateForAgent]
  );

  const startGeneration = useCallback(
    async (args: StartGenerationArgs): Promise<StartGenerationResult> => {
      try {
        const job = await startVersionGeneration(args.agentId, {
          is_public: args.isPublic,
          generate: args.generate,
          version: args.version,
          ...(args.saveAsDraft ? { save_as_draft: true } : {}),
        });
        // The start response may not carry the agent name yet — prefer the
        // caller-provided one for the popup label.
        upsertJob({ ...job, agent_name: job.agent_name || args.agentName });
        return { outcome: 'accepted', job };
      } catch (error) {
        // Only endpoint-confirmed pre-creation failures are safe to retry:
        // malformed/source validation (400), authentication/authorization
        // (401/403), missing agent (404), and a stale editor baseline (409,
        // checked at enqueue before any job exists). Other statuses and
        // response-less failures are ambiguous because a job identity may have
        // been lost.
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        if (status === 409) return { outcome: 'stale' };
        const definitelyRejected = status !== undefined && [400, 401, 403, 404].includes(status);
        return { outcome: definitelyRejected ? 'rejected' : 'ambiguous' };
      }
    },
    [upsertJob]
  );

  // --- Resume in-flight jobs on mount / workspace change --------------------
  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    listActiveVersionGenerations()
      .then((active) => {
        if (cancelled || active.length === 0) return;
        setJobs((prev) => {
          const next = { ...prev };
          for (const job of active) next[job.id] = job;
          return next;
        });
      })
      .catch(() => {
        /* no active jobs / endpoint unavailable — ignore */
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  // Visible (non-dismissed) jobs, newest first.
  const visibleJobs = useMemo(
    () =>
      Object.values(jobs)
        .filter((j) => !dismissed.has(j.id))
        .sort((a, b) => (a.created_at < b.created_at ? 1 : -1)),
    [jobs, dismissed]
  );

  const activeJobs = useMemo(() => Object.values(jobs).filter((j) => !isTerminalGenerationStatus(j.status)), [jobs]);
  const hasActiveJobs = activeJobs.length > 0;

  // Stable signature of active job ids so the polling effect only resets when
  // the set actually changes.
  const activeJobsSignature = useMemo(
    () =>
      activeJobs
        .map((j) => `${j.agent_id}:${j.id}`)
        .sort()
        .join('|'),
    [activeJobs]
  );

  // --- WebSocket: live progress --------------------------------------------
  const socketPath = workspaceId && hasActiveJobs ? `ws/workspace/${workspaceId}/events/` : null;

  useWebsockets(socketPath, {
    onMessage: (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type !== 'agent.version.event' || !data.payload) return;
        const payload = data.payload as Record<string, unknown>;
        const jobId = payload.job_id as string | undefined;
        const agentId = payload.agent_id as string | undefined;
        if (!jobId || !agentId) return;
        // Fetch the authoritative job (the WS payload omits the prompt body).
        refreshJob(agentId, jobId);
      } catch {
        /* ignore malformed frames */
      }
    },
  });

  // --- Polling fallback -----------------------------------------------------
  useEffect(() => {
    if (!activeJobsSignature) return;
    const startedAt = Date.now();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const getNextDelay = () => {
      const elapsedBackoffSteps = Math.floor((Date.now() - startedAt) / GENERATION_POLL_BACKOFF_INTERVAL_MS);
      return Math.min(
        GENERATION_POLL_INITIAL_DELAY_MS + elapsedBackoffSteps * GENERATION_POLL_BACKOFF_STEP_MS,
        GENERATION_POLL_MAX_DELAY_MS
      );
    };

    const tick = () => {
      for (const job of Object.values(jobs)) {
        if (!isTerminalGenerationStatus(job.status)) {
          refreshJob(job.agent_id, job.id);
        }
      }
      timeoutId = setTimeout(tick, getNextDelay());
    };

    timeoutId = setTimeout(tick, getNextDelay());
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
    // `jobs` is intentionally omitted: we key the interval on the active
    // signature and keep the original polling behavior for the current active set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJobsSignature, refreshJob]);

  const handleDismiss = useCallback((jobId: string) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(jobId);
      return next;
    });
  }, []);

  const handleView = useCallback((job: VersionGenerationJob) => {
    setOpenJob(job);
  }, []);

  const hasActiveGeneration = useCallback(
    (agentId: string) => activeJobs.some((job) => job.agent_id === agentId),
    [activeJobs]
  );
  const contextValue = useMemo<AgentVersionGenerationContextValue>(
    () => ({ startGeneration, hasActiveGeneration }),
    [startGeneration, hasActiveGeneration]
  );

  return (
    <AgentVersionGenerationContext.Provider value={contextValue}>
      {children}
      {showPopups && <AgentVersionGenerationPopups jobs={visibleJobs} onView={handleView} onDismiss={handleDismiss} />}
      <VersionPromptModal
        job={openJob}
        onClose={() => setOpenJob(null)}
        onResolved={(saved) => {
          // A compliance-blocked job became a real version — mirror what
          // refreshJob does on a terminal non-failed status so the editor's
          // version list / linked memories pick it up immediately.
          upsertJob(saved);
          invalidateForAgent(saved.agent_id);
        }}
      />
    </AgentVersionGenerationContext.Provider>
  );
}

export function useAgentVersionGeneration(): AgentVersionGenerationContextValue {
  const ctx = useContext(AgentVersionGenerationContext);
  if (!ctx) {
    throw new Error('useAgentVersionGeneration must be used within AgentVersionGenerationProvider');
  }
  return ctx;
}
